import { Alert, Button, Descriptions, Input, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import {
  RewardRedemption,
  RewardRedemptionStatus,
} from "../../repository/data.default";
import {
  approveRedemption,
  fulfillRedemption,
  rejectRedemption,
} from "../../repository/data.repository";
import { redemptionStatusColor } from "./RewardRedemptionsView.default";
import InModal from "../../controls/InModal";
import ManageStatusBanner from "../../controls/ManageStatusBanner";
import { useDialogAction } from "../../hooks/useDialogAction";
import { fmtDateTime as fmt } from "../../utils/date";

export interface RewardRedemptionManageDialogProps {
  readonly open?: boolean;
  readonly record?: RewardRedemption;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

// State → one-line guidance: what the redemption is waiting on and the next move.
const guidanceFor = (status?: string): string => {
  switch (status) {
    case RewardRedemptionStatus.Pending:
      return "Awaiting review — approve to proceed, or reject to refund the points.";
    case RewardRedemptionStatus.Approved:
      return "Approved — fulfil once the reward has been delivered to the user.";
    case RewardRedemptionStatus.Fulfilled:
      return "Delivered — this redemption is complete.";
    case RewardRedemptionStatus.Rejected:
      return "Rejected — the points were refunded to the user.";
    case RewardRedemptionStatus.Cancelled:
      return "Cancelled by the requester — the points were refunded.";
    default:
      return "";
  }
};

function RewardRedemptionManageDialog(props: RewardRedemptionManageDialogProps) {
  const { loading, setLoading, errorMessage, setErrorMessage, run } =
    useDialogAction({ onSuccess: props.onFinish });
  const [notes, setNotes] = useState("");

  const status = props.record?.status;
  const canApprove = status === RewardRedemptionStatus.Pending;
  const canFulfill = status === RewardRedemptionStatus.Approved;
  const canReject =
    status === RewardRedemptionStatus.Pending ||
    status === RewardRedemptionStatus.Approved;

  useEffect(() => {
    if (props.open) {
      setErrorMessage("");
      setLoading(false);
      setNotes(props.record?.notes ?? "");
    }
  }, [props.open]);

  return (
    <InModal
      open={props.open}
      size="medium"
      title="Manage Redemption"
      onCancel={props.onCancel}
      actions={
        <Space>
          <Button disabled={loading} onClick={props.onCancel}>
            Close
          </Button>
          {canApprove && (
            <Button
              type="primary"
              loading={loading}
              onClick={() => run(() => approveRedemption(props.record!.uid))}
            >
              Approve
            </Button>
          )}
          {canFulfill && (
            <Button
              type="primary"
              loading={loading}
              onClick={() => run(() => fulfillRedemption(props.record!.uid))}
            >
              Fulfil
            </Button>
          )}
          {canReject && (
            <Button
              danger
              loading={loading}
              onClick={() =>
                run(() => rejectRedemption(props.record!.uid, { notes }))
              }
            >
              Reject &amp; refund
            </Button>
          )}
        </Space>
      }
    >
      {/* Status banner + contextual guidance */}
      <ManageStatusBanner
        tagColor={redemptionStatusColor(status)}
        status={status}
        guidance={guidanceFor(status)}
      />

      <Descriptions
        size="small"
        column={1}
        bordered
        items={[
          { key: "user", label: "User", children: props.record?.userUserName ?? "—" },
          { key: "reward", label: "Reward", children: props.record?.rewardName ?? "—" },
          { key: "points", label: "Points spent", children: props.record?.pointsSpent ?? "—" },
          { key: "company", label: "Company", children: props.record?.companyName ?? "—" },
          { key: "requestedOn", label: "Requested On", children: fmt(props.record?.requestedOn) },
          { key: "decidedOn", label: "Decided On", children: fmt(props.record?.decidedOn) },
          { key: "decidedBy", label: "Decided By", children: props.record?.decidedByUserUserName ?? "—" },
        ]}
      />

      {/* Rejection note — optional, surfaced while the redemption can still be rejected. */}
      {canReject && (
        <div className="mt-4">
          <Typography.Text className="mb-1 block font-medium">
            Decision note (shown on reject)
          </Typography.Text>
          <Input.TextArea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional — reason for rejection…"
          />
        </div>
      )}

      {errorMessage && (
        <Alert className="mt-3" message={errorMessage} type="error" />
      )}
    </InModal>
  );
}

export default RewardRedemptionManageDialog;
